import { useState } from 'react';
import { Button, Popover, PopoverContent, PopoverTrigger } from '@predoc/cow';
import { ChevronDown } from 'lucide-react';
import { ProfileFilterList } from '@/feature/patients/components/patient-list/ProfileFilterList';
import { getHasActiveProfileFilter } from '@/feature/patients/components/patient-list/profileFilter';
import type { ProfileFilterState } from '@/feature/patients/types';
import { useListProfiles } from '@/feature/profiles/hooks/profilesAPI';
import { cn } from '@/lib/utils';

interface EfaxProfileSelectorProps {
  value: ProfileFilterState;
  onChange: (profileFilter: ProfileFilterState) => void;
}

const formatProfileCountLabel = (count: number) =>
  count === 1 ? '1 profile' : `${count} profiles`;

const getTriggerLabel = (
  profileFilter: ProfileFilterState,
  totalProfileCount: number,
) => {
  if (!getHasActiveProfileFilter(profileFilter, totalProfileCount)) {
    return 'Any profile';
  }

  if (profileFilter.profileFilterMode === 'include') {
    if (profileFilter.organizationProfileIds.length === 0) {
      return 'No profiles';
    }

    return formatProfileCountLabel(profileFilter.organizationProfileIds.length);
  }

  const excludedCount = profileFilter.excludeOrganizationProfileIds.length;
  if (excludedCount === 0) {
    return 'Any profile';
  }

  return formatProfileCountLabel(
    Math.max(totalProfileCount - excludedCount, 0),
  );
};

export const EfaxProfileSelector = ({
  value,
  onChange,
}: EfaxProfileSelectorProps) => {
  const [open, setOpen] = useState(false);

  // Only used for the total profile count, so limit is 1.
  const { data: totalData } = useListProfiles({
    assignedProfilesOnly: true,
    pagination: { offset: 0, limit: 1 },
  });
  const totalProfileCount = totalData?.totalCount ?? 0;

  const hasActiveFilters = getHasActiveProfileFilter(value, totalProfileCount);
  const triggerLabel = getTriggerLabel(value, totalProfileCount);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className={cn(
            'border-input bg-background h-9 w-full justify-between rounded-md border px-3 font-normal',
            hasActiveFilters && 'border-blue-400 bg-blue-50',
          )}
          aria-label="Select profiles"
        >
          <span
            className={cn(
              'truncate text-sm',
              hasActiveFilters ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            {triggerLabel}
          </span>
          <ChevronDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        {open && (
          <ProfileFilterList
            profileFilter={value}
            onProfileFilterChange={onChange}
            totalProfileCount={totalProfileCount}
            listClassName="max-h-60"
            autoFocusSearch
          />
        )}
      </PopoverContent>
    </Popover>
  );
};
